import {
  ArrowRight,
  BarChart3,
  Calculator,
  CheckCircle2,
  Gauge,
  Mail,
  ShieldCheck
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createLead, loadPublicConfig } from "./api";
import type { LeadInput, PublicConfig } from "./types";

type PricingInputs = {
  monthlyCosts: number;
  targetCustomers: number;
  targetMargin: number;
  competitorLow: number;
  competitorHigh: number;
  currentPrice: number;
  churnRate: number;
  cac: number;
};

const defaultInputs: PricingInputs = {
  monthlyCosts: 2500,
  targetCustomers: 100,
  targetMargin: 80,
  competitorLow: 19,
  competitorHigh: 59,
  currentPrice: 29,
  churnRate: 5,
  cac: 320
};

const TRAFFIC_PAGES: Record<
  string,
  {
    title: string;
    eyebrow: string;
    intro: string;
    sections: Array<{ heading: string; body: string }>;
  }
> = {
  "/pricing-lab-tools/pricing-page-teardown/": {
    eyebrow: "Pricing page teardown",
    intro:
      "Use this checklist to review whether your SaaS pricing page explains value, reduces buyer anxiety and supports a real pricing conversation.",
    sections: [
      {
        body:
          "A pricing page should make the target user obvious. If the page lists features without naming the buyer, visitors cannot tell whether the plan was designed for them.",
        heading: "Start with buyer clarity"
      },
      {
        body:
          "Each tier should map to a different stage, workload or constraint. Avoid tiers that only differ by arbitrary feature removal.",
        heading: "Make tier logic visible"
      },
      {
        body:
          "Show what changes when a customer moves up a plan: saved time, higher usage, reporting, support, governance or integration depth.",
        heading: "Connect price to value"
      },
      {
        body:
          "Before scaling paid acquisition, sanity-check CAC payback, churn and LTV:CAC. A pricing page can convert while still producing weak unit economics.",
        heading: "Check economics before traffic"
      }
    ],
    title: "SaaS Pricing Page Teardown Checklist"
  },
  "/pricing-lab-tools/saas-pricing-calculator/": {
    eyebrow: "SaaS pricing calculator",
    intro:
      "A practical walkthrough for using cost floor, market corridor and unit economics to set a SaaS price range worth testing.",
    sections: [
      {
        body:
          "Your cost floor is the minimum price needed to cover operating costs at your target customer count while preserving gross margin.",
        heading: "Calculate the cost floor"
      },
      {
        body:
          "Competitor prices are not a command. They are a corridor. Use them to understand buyer expectations, then adjust for your positioning and value.",
        heading: "Use competitors as a range"
      },
      {
        body:
          "The first price is a test. Pick a range, talk to real customers and watch objections before polishing the pricing page.",
        heading: "Test a range, not one number"
      }
    ],
    title: "How To Use A SaaS Pricing Calculator"
  },
  "/pricing-lab-tools/saas-unit-economics-calculator/": {
    eyebrow: "SaaS unit economics",
    intro:
      "Use LTV:CAC, churn and CAC payback to check whether your SaaS pricing can support profitable acquisition.",
    sections: [
      {
        body:
          "LTV is driven by revenue per account, gross margin and churn. A higher price only helps if customers stay long enough.",
        heading: "Estimate customer lifetime value"
      },
      {
        body:
          "CAC payback shows how many months it takes to recover acquisition cost from gross profit. Long payback creates cash pressure.",
        heading: "Watch CAC payback"
      },
      {
        body:
          "A healthy model usually needs enough margin and retention to leave room for support, product development and distribution costs.",
        heading: "Do not ignore margin"
      }
    ],
    title: "SaaS Unit Economics Calculator Guide"
  }
};

function currency(value: number) {
  if (!Number.isFinite(value)) return "$0";
  return new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 0,
    style: "currency"
  }).format(Math.max(0, value));
}

function numberValue(value: FormDataEntryValue | null, fallback: number) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function PricingLabPage() {
  const [inputs, setInputs] = useState<PricingInputs>(defaultInputs);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [leadStatus, setLeadStatus] = useState("Get the pricing teardown template.");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const trafficPage = TRAFFIC_PAGES[window.location.pathname];

  useEffect(() => {
    document.title = trafficPage
      ? `${trafficPage.title} | Pricing Lab Tools`
      : "Free SaaS Pricing Calculator | Pricing Lab Tools";
  }, [trafficPage]);

  useEffect(() => {
    void loadPublicConfig()
      .then(setConfig)
      .catch(() => {
        setConfig({
          analytics: {
            customEventsEnabled: true,
            mode: "not_configured",
            webAnalyticsToken: null
          },
          turnstile: {
            enabled: false,
            siteKey: null
          }
        });
      });
  }, []);

  useEffect(() => {
    const siteKey = config?.turnstile.siteKey;
    const element = turnstileRef.current;
    if (!siteKey || !element || turnstileWidgetId.current) return;
    const siteKeyValue = siteKey;

    function renderTurnstile() {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current) return;

      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        callback: (token) => setTurnstileToken(token),
        sitekey: siteKeyValue,
        theme: "light"
      });
    }

    const existing = document.querySelector<HTMLScriptElement>("script[data-siteops-turnstile='true']");

    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    if (existing) {
      existing.addEventListener("load", renderTurnstile);
      return () => existing.removeEventListener("load", renderTurnstile);
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.siteopsTurnstile = "true";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.addEventListener("load", renderTurnstile);
    document.head.appendChild(script);

    return () => script.removeEventListener("load", renderTurnstile);
  }, [config?.turnstile.siteKey]);

  const model = useMemo(() => {
    const customerCount = Math.max(1, inputs.targetCustomers);
    const margin = Math.min(95, Math.max(5, inputs.targetMargin)) / 100;
    const churn = Math.max(0.1, inputs.churnRate) / 100;
    const costPerCustomer = inputs.monthlyCosts / customerCount;
    const costFloor = costPerCustomer / Math.max(0.05, 1 - margin);
    const marketMidpoint = (inputs.competitorLow + inputs.competitorHigh) / 2;
    const weightedPrice = costFloor * 0.45 + marketMidpoint * 0.35 + inputs.currentPrice * 0.2;
    const recommendedPrice = Math.max(9, Math.round(weightedPrice / 5) * 5);
    const lowTest = Math.max(9, Math.round((recommendedPrice * 0.8) / 5) * 5);
    const highTest = Math.max(lowTest + 5, Math.round((recommendedPrice * 1.25) / 5) * 5);
    const starter = Math.max(9, Math.round((recommendedPrice * 0.6) / 5) * 5);
    const growth = recommendedPrice;
    const business = Math.max(growth + 10, Math.round((recommendedPrice * 2.1) / 5) * 5);
    const monthlyRevenue = recommendedPrice * customerCount;
    const grossProfit = monthlyRevenue * margin;
    const ltv = (recommendedPrice * margin) / churn;
    const paybackMonths = inputs.cac / Math.max(1, recommendedPrice * margin);
    const ltvToCac = ltv / Math.max(1, inputs.cac);
    const verdict =
      ltvToCac >= 3 && paybackMonths <= 12
        ? "Strong first-pass economics"
        : ltvToCac >= 2 && paybackMonths <= 18
          ? "Needs validation before scaling"
          : "Rework pricing or acquisition";

    return {
      business,
      costFloor,
      grossProfit,
      growth,
      highTest,
      lowTest,
      ltv,
      ltvToCac,
      marketMidpoint,
      monthlyRevenue,
      paybackMonths,
      recommendedPrice,
      starter,
      verdict
    };
  }, [inputs]);

  function updateInput(event: FormEvent<HTMLFormElement>) {
    const form = new FormData(event.currentTarget);
    setInputs({
      monthlyCosts: numberValue(form.get("monthlyCosts"), defaultInputs.monthlyCosts),
      targetCustomers: numberValue(form.get("targetCustomers"), defaultInputs.targetCustomers),
      targetMargin: numberValue(form.get("targetMargin"), defaultInputs.targetMargin),
      competitorLow: numberValue(form.get("competitorLow"), defaultInputs.competitorLow),
      competitorHigh: numberValue(form.get("competitorHigh"), defaultInputs.competitorHigh),
      currentPrice: numberValue(form.get("currentPrice"), defaultInputs.currentPrice),
      churnRate: numberValue(form.get("churnRate"), defaultInputs.churnRate),
      cac: numberValue(form.get("cac"), defaultInputs.cac)
    });
  }

  async function captureLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const payload: LeadInput = {
      company_stage: String(form.get("company_stage") ?? ""),
      email: String(form.get("email") ?? ""),
      pricing_problem: String(form.get("pricing_problem") ?? ""),
      role: String(form.get("role") ?? ""),
      site_id: "site_pricing_lab",
      source_path: window.location.pathname,
      turnstile_token: turnstileToken,
      website: String(form.get("website") ?? "")
    };

    try {
      const result = await createLead(payload);
      setLeadStatus(
        result.turnstile_status === "verified"
          ? "Saved. Turnstile verified this request."
          : "Saved. Turnstile is not configured yet, so this request is marked for manual review."
      );
      event.currentTarget.reset();
      setTurnstileToken(null);
      if (turnstileWidgetId.current) window.turnstile?.reset(turnstileWidgetId.current);
    } catch {
      setLeadStatus("Could not save yet. Email hello@pricinglab.tools manually.");
    }
  }

  return (
    <main className="public-page">
      <header className="public-nav">
        <a className="public-logo" href="/pricing-lab-tools/" aria-label="Pricing Lab Tools home">
          PL
        </a>
        <nav>
          <a href="#calculator">Calculator</a>
          <a href="#model">Model</a>
          <a href="#template">Template</a>
          <a href="/pricing-lab-tools/saas-unit-economics-calculator/">Unit economics</a>
          <a href="/">Operator console</a>
        </nav>
      </header>

      {trafficPage && (
        <section className="content-article">
          <p className="public-eyebrow">{trafficPage.eyebrow}</p>
          <h1>{trafficPage.title}</h1>
          <p>{trafficPage.intro}</p>
          <div className="article-sections">
            {trafficPage.sections.map((section) => (
              <article key={section.heading}>
                <h2>{section.heading}</h2>
                <p>{section.body}</p>
              </article>
            ))}
          </div>
          <a className="primary-link" href="/pricing-lab-tools/#calculator-form">
            Run the calculator
            <ArrowRight size={16} />
          </a>
        </section>
      )}

      <section className="tool-hero" id="calculator">
        <div className="tool-copy">
          <p className="public-eyebrow">Free SaaS pricing calculator</p>
          <h1>Find a practical SaaS price range before you launch new plans.</h1>
          <p>
            Model cost floor, competitor corridor, gross margin, churn, CAC payback and
            LTV:CAC in one place. Built for bootstrapped founders who need a pricing
            decision, not a 40-page strategy deck.
          </p>
          <div className="hero-actions">
            <a className="primary-link" href="#calculator-form">
              Run the model
              <ArrowRight size={16} />
            </a>
            <a className="secondary-link" href="#template">Get teardown template</a>
          </div>
          <div className="proof-strip">
            <span>
              <ShieldCheck size={16} />
              No financial advice
            </span>
            <span>
              <CheckCircle2 size={16} />
              Editable assumptions
            </span>
            <span>
              <BarChart3 size={16} />
              Unit economics first
            </span>
          </div>
        </div>

        <section className="calculator-panel" aria-label="SaaS pricing calculator">
          <div className="panel-title">
            <Calculator size={22} />
            <div>
              <strong>Pricing model</strong>
              <span>Change any input to update the recommendation.</span>
            </div>
          </div>

          <form className="pricing-form" id="calculator-form" onInput={updateInput}>
            <label>
              Monthly operating costs
              <input defaultValue={inputs.monthlyCosts} min="0" name="monthlyCosts" type="number" />
            </label>
            <label>
              Target paying customers
              <input defaultValue={inputs.targetCustomers} min="1" name="targetCustomers" type="number" />
            </label>
            <label>
              Target gross margin %
              <input defaultValue={inputs.targetMargin} max="95" min="5" name="targetMargin" type="number" />
            </label>
            <label>
              Current / planned price
              <input defaultValue={inputs.currentPrice} min="0" name="currentPrice" type="number" />
            </label>
            <label>
              Competitor low price
              <input defaultValue={inputs.competitorLow} min="0" name="competitorLow" type="number" />
            </label>
            <label>
              Competitor high price
              <input defaultValue={inputs.competitorHigh} min="0" name="competitorHigh" type="number" />
            </label>
            <label>
              Monthly churn %
              <input defaultValue={inputs.churnRate} min="0.1" name="churnRate" step="0.1" type="number" />
            </label>
            <label>
              CAC per customer
              <input defaultValue={inputs.cac} min="0" name="cac" type="number" />
            </label>
          </form>
        </section>
      </section>

      <section className="model-grid" id="model">
        <article className="result-panel primary-result">
          <span>Suggested test range</span>
          <strong>
            {currency(model.lowTest)}-{currency(model.highTest)}
          </strong>
          <p>
            Start with a paid test corridor instead of treating the model as a final
            answer. Interview users before expanding tiers.
          </p>
        </article>
        <article className="result-panel">
          <span>Recommended anchor</span>
          <strong>{currency(model.recommendedPrice)}</strong>
          <p>Weighted from cost floor, market midpoint and your current price.</p>
        </article>
        <article className="result-panel">
          <span>CAC payback</span>
          <strong>{model.paybackMonths.toFixed(1)} mo</strong>
          <p>Internal heuristic from your CAC and gross profit per account.</p>
        </article>
        <article className="result-panel">
          <span>LTV:CAC</span>
          <strong>{model.ltvToCac.toFixed(1)}x</strong>
          <p>{model.verdict}</p>
        </article>
      </section>

      <section className="pricing-tiers" aria-label="Draft pricing tiers">
        <div>
          <p className="public-eyebrow">Draft tier structure</p>
          <h2>Turn the model into a pricing page test.</h2>
          <p>
            Use these tiers as a starting point for customer conversations. The goal is
            to learn willingness to pay before building extra product scope.
          </p>
        </div>
        {[
          ["Starter", model.starter, "For solo operators validating one workflow."],
          ["Growth", model.growth, "For active teams that need recurring usage."],
          ["Business", model.business, "For teams with reporting, support or governance needs."]
        ].map(([name, price, detail]) => (
          <article className="tier" key={String(name)}>
            <span>{name}</span>
            <strong>{currency(Number(price))}/mo</strong>
            <p>{detail}</p>
          </article>
        ))}
      </section>

      <section className="decision-section">
        <div className="decision-copy">
          <p className="public-eyebrow">What this model checks</p>
          <h2>Pricing is a system, not one number.</h2>
          <p>
            A price only works if it covers delivery costs, fits the market corridor,
            supports acquisition payback and matches the value customers believe they get.
          </p>
        </div>
        <div className="formula-list">
          <div>
            <Gauge size={20} />
            <strong>Cost floor</strong>
            <span>Monthly costs / target customers / margin gap.</span>
          </div>
          <div>
            <BarChart3 size={20} />
            <strong>Market midpoint</strong>
            <span>The editable competitor range you enter.</span>
          </div>
          <div>
            <Calculator size={20} />
            <strong>Unit economics</strong>
            <span>MRR, gross profit, payback and LTV:CAC.</span>
          </div>
        </div>
      </section>

      <section className="lead-section" id="template">
        <div>
          <p className="public-eyebrow">Pricing teardown template</p>
          <h2>Want the one-page pricing review worksheet?</h2>
          <p>
            Leave your email and the pricing problem you are trying to solve. This stores
            your request for manual follow-up; there is no automated newsletter yet.
          </p>
        </div>
        <form className="lead-form" onSubmit={captureLead}>
          <input autoComplete="off" className="website-field" name="website" tabIndex={-1} />
          <label>
            Work email
            <input name="email" placeholder="founder@company.com" required type="email" />
          </label>
          <label>
            Your role
            <input name="role" placeholder="Founder, PM, growth lead..." required />
          </label>
          <label>
            Company stage
            <select name="company_stage" defaultValue="Pre-launch">
              <option>Pre-launch</option>
              <option>Early revenue</option>
              <option>Scaling plans</option>
              <option>Repricing existing product</option>
            </select>
          </label>
          <label>
            Pricing problem
            <textarea
              name="pricing_problem"
              placeholder="What are you trying to decide: first price, tier split, annual discount, usage-based pricing?"
              required
            />
          </label>
          {config?.turnstile.siteKey && <div className="turnstile-slot" ref={turnstileRef} />}
          <button type="submit">
            <Mail size={16} />
            Request the template
          </button>
          <p>{leadStatus}</p>
        </form>
      </section>

      <footer className="public-footer">
        <strong>Pricing Lab Tools</strong>
        <span>
          Planning calculator only. Validate pricing with real customer conversations
          before changing production plans.
        </span>
      </footer>
    </main>
  );
}
